// AVRASTRO sync protocol — shared by the device client and the reference server.
//
// Unit of replication: one operation per row change, produced by the Workspace in the same
// transaction as the change itself (durable outbox). Ordering is the server's sequence number,
// never device clocks (clock skew cannot reorder history). Every operation is idempotent by id.
import { TABLE_NAMES, isLocalTable, type TableName } from '@vero/domain';

export const PROTOCOL_VERSION = 1;

// --- requirement 21: minimum client version handshake ------------------------------------------------------------------
// Every sync request of a current app says which data schema and app version it is (headers below). The server answers
// 426 `client_outdated` to push, pull and file transfers from a client older than the configured minimum: that client
// pauses sync and asks for an update — it never uploads or downloads data it cannot represent, and its unsent changes
// stay in its outbox until it is updated. Clients from before the handshake send no headers: they count as
// LEGACY_CLIENT_SCHEMA (the 0.7.1 desktop apps in the field are schema 11), so the default minimum lets them keep working.
export const CLIENT_SCHEMA_HEADER = 'x-vero-schema';
export const CLIENT_APP_HEADER = 'x-vero-app';
/** the oldest data schema the sync server still accepts by default (the desktop apps in the field in October 2026) */
export const OLDEST_SUPPORTED_SCHEMA = 11;
/** a client that sends no schema header predates the handshake (the 0.7.1 desktop release is schema 11) */
export const LEGACY_CLIENT_SCHEMA = 11;
export interface MinClient { schema: number; app: string | null }
export const DEFAULT_MIN_CLIENT: MinClient = { schema: OLDEST_SUPPORTED_SCHEMA, app: null };
export interface ClientInfo { schema: number; app: string }
export function versionCmp(a: string, b: string): number {
  const pa = a.split(/[.+-]/).map((x) => Number.parseInt(x, 10) || 0), pb = b.split(/[.+-]/).map((x) => Number.parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const d = (pa[i] ?? 0) - (pb[i] ?? 0); if (d) return d < 0 ? -1 : 1; }
  return 0;
}
/** the client as its request headers describe it (no header = a pre-handshake client) */
export function clientFromHeaders(h: Record<string, string | string[] | undefined>): { schema: number; app: string | null } {
  const one = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v) ?? '';
  const schema = Number.parseInt(one(h[CLIENT_SCHEMA_HEADER]), 10);
  const app = one(h[CLIENT_APP_HEADER]).trim().slice(0, 40);
  return { schema: Number.isFinite(schema) && schema > 0 ? schema : LEGACY_CLIENT_SCHEMA, app: /^\d+(\.\d+){0,3}([+-][\w.]+)?$/.test(app) ? app : null };
}
/** true when the server must refuse this client's sync (an unknown app version counts as older than a set minimum) */
export function clientOutdated(c: { schema: number; app: string | null }, min: MinClient): boolean {
  if (c.schema < min.schema) return true;
  return !!min.app && (!c.app || versionCmp(c.app, min.app) < 0);
}

export interface WireOp {
  opId: string; csId: string; deviceId: string; table: string; rowId: string; kind: 'put' | 'delete';
  baseVersion: number; rowVersion: number; fields: string[]; row?: Record<string, unknown> | null; at: string;
  /** v13: signed single-use step-up grant (platform) for a policy deletion or restore; never stored by the server */
  grant?: string | null;
}
export interface PulledOp extends WireOp { seq: number; userId?: string }
export type PushStatus = 'ok' | 'duplicate' | 'conflict' | 'forbidden' | 'invalid';
export interface PushResult { opId: string; status: PushStatus; seq?: number; version?: number; serverRow?: Record<string, unknown> | null; serverVersion?: number; serverFields?: string[]; error?: string }
export interface PullResult { ops: PulledOp[]; last: number; more: boolean }
export interface LoginResult { token: string; userId: string; role: Role; workspaceId: string; workspaceName: string; expiresAt: string; leaseUntil: string }

// --- roles and permissions (spec §98) ---------------------------------------------------------
// Enforced by the server on push, pull and file download. The UI may hide things as a convenience,
// but hiding is never the access control.
export const ROLES = ['admin', 'advisor', 'manager', 'assistant'] as const;
export type Role = typeof ROLES[number];
export type Action = 'read' | 'write';

/** workspace configuration only an admin may change */
const ADMIN_WRITE: ReadonlySet<string> = new Set(['custom_fields', 'tax_rulesets', 'commission_rules']);
/** a manager sees activity, pipeline and production — not private client documents, notes or finances */
const MANAGER_READ: ReadonlySet<string> = new Set(['persons', 'households', 'household_members', 'policies', 'policy_adjustments', 'production_events', 'renewals', 'tasks', 'reviews', 'cases', 'case_events', 'case_requirements', 'campaigns', 'campaign_activities', 'opportunities', 'prospect_lists', 'prospects', 'referrals', 'quotations', 'commission_entries', 'commission_rules', 'settings', 'custom_fields', 'tax_rulesets', 'catalog_insurers', 'catalog_categories', 'catalog_plans']);
/** an assistant runs the agenda and contacts — no financial data, tax, commission or sensitive documents */
// v15: daily_reports are the advisor's own end-of-day self-evaluation (self-rating, notes): not for assistants; managers
// do not read them either (not in MANAGER_READ)
const ASSISTANT_DENY: ReadonlySet<string> = new Set(['financial_items', 'tax_snapshots', 'commission_rules', 'commission_entries', 'extraction_drafts', 'needs_analyses', 'production_events', 'daily_reports']);
const ASSISTANT_NO_WRITE: ReadonlySet<string> = new Set(['policies', 'policy_adjustments', 'claims', 'renewals', 'tax_rulesets', 'custom_fields', 'automation_rules', 'settings', 'comparisons', 'quotations', 'catalog_insurers', 'catalog_categories', 'catalog_plans']);
const SENSITIVE_DOC_CATEGORIES: ReadonlySet<string> = new Set(['medical', 'income', 'tax', 'loans']);

export function isSensitiveDocument(row: Record<string, unknown> | null | undefined): boolean {
  return !!row && (row.restricted === true || SENSITIVE_DOC_CATEGORIES.has(String(row.category ?? '')));
}

export function can(role: Role, action: Action, table: string, row?: Record<string, unknown> | null): boolean {
  if (!(TABLE_NAMES as readonly string[]).includes(table) || isLocalTable(table)) return false;
  switch (role) {
    case 'admin': return true;
    case 'advisor': return action === 'read' || !ADMIN_WRITE.has(table);
    case 'manager':
      if (action === 'write') return false; // oversight role: read-only
      return MANAGER_READ.has(table);
    case 'assistant':
      if (ASSISTANT_DENY.has(table)) return false;
      if (table === 'documents' && isSensitiveDocument(row)) return false;
      if (action === 'write') return !ASSISTANT_NO_WRITE.has(table) && !ADMIN_WRITE.has(table);
      return true;
  }
}

// --- v18: private rows — the advisor's own tax folder («Ο φορολογικός μου φάκελος») -------------------------------------
// These rows belong to ONE member, whatever the roles: the server serves them only to the member who first stored them
// (the author of the row's first operation) and, read-only, to members whose e-mail the owner listed in the year's
// `sharedWith`. Admins, managers and assistants are no exception. Nobody else may write them, and nobody may put a row
// into another member's folder. `can()` (roles) does not apply to them; SyncServer.allowed does this check.
export const PRIVATE_TABLES: ReadonlySet<string> = new Set(['tax_years', 'tax_items', 'tax_expected']);
/**
 * The tax year (tax_years.id) a private row belongs to, or null when the row is not private. A deleted row (no row)
 * of a private table counts as private with an unknown year (''), as does the audit event of a tax-folder change
 * (readable by its author only).
 */
export function privateYearId(table: string, row: Record<string, unknown> | null | undefined): string | null {
  if (table === 'tax_years') return String(row?.id ?? '');
  if (table === 'tax_items' || table === 'tax_expected') return String(row?.yearId ?? '');
  if (table === 'documents' && row && typeof row.taxYearId === 'string' && row.taxYearId) return row.taxYearId;
  // the local audit trail of a tax-folder change (its summary names the issuer): private to its author, never shared
  if (table === 'audit_events' && row && (/^(undo:)*taxf\./.test(String(row.action ?? '')) || PRIVATE_TABLES.has(String(row.entity ?? '')))) return '';
  return null;
}

/** Fields whose conflicting edits always need the advisor (never merged automatically). */
export const CRITICAL_FIELDS: Partial<Record<TableName, readonly string[]>> = {
  persons: ['firstName', 'lastName', 'displayName', 'birthDate', 'primaryPhone', 'primaryEmail', 'archivedAt'],
  policies: ['annualPremium', 'status', 'sumAssured', 'benefits', 'parties', 'paymentFrequency', 'instalment', 'currency', 'policyNumber'],
  policy_adjustments: ['annualAmount', 'date', 'newAnnual'],
  consents: ['granted', 'purpose', 'withdrawnAt', 'lawfulBasis'],
  financial_items: ['amount', 'balance', 'frequency', 'currency'],
  claims: ['status', 'amountClaimed', 'amountPaid'],
  commission_entries: ['expectedAmount', 'paidAmount', 'status'],
};
export const isCriticalField = (table: string, field: string) => !!CRITICAL_FIELDS[table as TableName]?.includes(field);

// --- v13: policy deletion is a secured transition -----------------------------------------------------------------
/** the soft-deletion fields of a policy; changing any of them needs a server step-up grant */
export const POLICY_DELETION_FIELDS = ['deletedAt', 'deletedBy', 'deleteReason'] as const;
export type PolicyTransition = 'delete' | 'restore';
export const STEPUP_ERRORS = ['stepup_required', 'stepup_invalid', 'stepup_expired', 'stepup_used', 'stepup_mismatch', 'stepup_unavailable', 'stepup_window', 'policy_hard_delete', 'policy_deleted_changed', 'policy_delete_fields'] as const;
/** the server refused a policy deletion/restore for lack of a valid grant (its row comes back so the device can revert) */
export const isStepUpRefusal = (r: Pick<PushResult, 'status' | 'error'>) => r.status === 'forbidden' && (STEPUP_ERRORS as readonly string[]).includes(String(r.error ?? ''));
const delState = (row: Record<string, unknown> | null | undefined) => POLICY_DELETION_FIELDS.map((f) => (row?.[f] ?? null) === '' ? null : row?.[f] ?? null);
/**
 * Which secured transition a policy change makes: 'delete' (not deleted → deleted), 'restore' (deleted → not deleted),
 * 'changed' (deleted → deleted with other deletion data: never allowed), or null (deletion state unchanged).
 * An older app that does not know the fields drops them from the row it sends, which reads as a restore — so it is
 * refused without a grant, and a deleted policy can never come back through an old client.
 */
export function policyTransition(current: Record<string, unknown> | null | undefined, next: Record<string, unknown> | null | undefined): PolicyTransition | 'changed' | null {
  const a = delState(current), b = delState(next);
  if (a.every((x, i) => x === b[i])) return null;
  if (!a[0] && b[0]) return 'delete';
  if (a[0] && !b[0]) return 'restore';
  return 'changed';
}
